import classNames from "classnames";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";

import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import { getFareConditions } from "shared/lib/golCopy";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";
import SecondaryButton from "@components/UI/SecondaryButton";

type Props = {
  getRefsForFlightOffer: () => string;
  error: boolean;
  confirmed: boolean;
  toggleConditions: () => void;
};

const TermsAndConditions: React.FC<Props> = ({
  getRefsForFlightOffer,
  error,
  confirmed,
  toggleConditions,
}): ReactElement => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const [conditions, setConditions]: [
    { content: string; title: string; alternateUrl?: string },
    React.Dispatch<
      React.SetStateAction<{
        content: string;
        title: string;
        alternateUrl?: string;
      }>
    >
  ] = useState({ content: "", title: "" });

  const printIframeRef = useRef<HTMLIFrameElement>();

  const loadData = async () => {
    const refs = getRefsForFlightOffer();
    const fareConditions = await getFareConditions(refs);
    setConditions({ ...fareConditions });
  };

  const handlePrint = () => {
    if (printIframeRef?.current) {
      printIframeRef.current.focus();
      printIframeRef.current.contentWindow.print();
    }
  };

  const onIframeLoad = () => {
    if (printIframeRef?.current) {
      printIframeRef.current.contentDocument.head.innerHTML = `
        <style>
          body { 
            color: #131f6b; 
            font-family: "Times New Roman", Times, serif; 
            line-height: 2; 
            font-size: 13px; 
          }
        </style>
      `;
    }
  };

  useEffect(() => {
    if (conditions?.alternateUrl) {
      window.open(conditions.alternateUrl, "_blank");
      setIsOpen(false);
      setConditions({ content: "", title: "" });
    }
  }, [conditions]);

  const setIsOpenCustom = async () => {
    dispatch(startUniversalLoading());
    await loadData();
    setIsOpen(true);

    dispatch(stopUniversalLoading());
  };

  const handleToggleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    toggleConditions();
  };

  const handleOpenConditionsKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    setIsOpenCustom();
  };

  const escapedContent = conditions?.content?.replace(
    /(?:\r\n|\r|\n)/g,
    "<br/>"
  );

  return (
    <div className="flex items-center">
      <div
        className="result-confirmation-choice flex items-center pointer"
        onClick={toggleConditions}
        onKeyDown={handleToggleKeyDown}
        role="checkbox"
        tabIndex={0}
        aria-checked={confirmed}
        aria-label={formatMessage({ id: "Result.agreeConditions" })}
      >
        <div className="result-confirmation-checkbox-wrapper">
          <Img
            src={`static/images/ico-checkbox-${
              confirmed ? "" : "un"
            }checked.svg`}
            className="result-confirmation-checkbox"
            alt=""
            role="presentation"
          />
        </div>
        <span
          className={classNames({
            "result-confirmation-text-red": error,
          })}
          style={{ userSelect: "none" }}
        >
          <FormattedMessage id="Result.agreeConditions" />
          &nbsp;
        </span>
      </div>
      <span
        className="link pointer nowrap"
        onClick={setIsOpenCustom}
        onKeyDown={handleOpenConditionsKeyDown}
        role="button"
        tabIndex={0}
      >
        <FormattedMessage id="Result.termsAndConditions" />
      </span>
      <Modal
        isActive={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <div className="container flight-conditions-terms">
          {!!conditions.content && (
            <div style={{ lineHeight: 2 }}>
              <SecondaryButton
                onClick={handlePrint}
                style={{
                  padding: "4px 24px",
                  marginBottom: "40px",
                  minWidth: 0,
                }}
              >
                <FormattedMessage id="Result.termsAndConditions.print" />
              </SecondaryButton>
              <div
                dangerouslySetInnerHTML={{
                  __html: escapedContent,
                }}
              />
              <iframe
                title="Print iframe"
                ref={printIframeRef}
                onLoad={onIframeLoad}
                style={{ display: "none" }}
                srcDoc={escapedContent}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TermsAndConditions;
